import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router";
import {
  CollectionContext,
  OrganizationDataRecipientDto,
} from "../../../../common/service/server/dto/OrganizationDataRecipientDto";
import { OrganizationDto } from "../../../../common/service/server/dto/OrganizationDto";
import { Button, Modal, SimpleCheckboxControlled } from "@truevault/aquilo";
import { Disableable } from "../../../components/Disableable";
import { Api } from "../../../../common/service/Api";
import {
  getDefaultEmailBodyForVendor,
  getMissingVendorDocumentationProperties,
  MISSING_DOC_LABELS,
} from "./vendorDocumentationEmailUtils";
import { XCircleIcon } from "@heroicons/react/24/solid";
import {
  canSendPrivacyEmail,
  PRIVACY_EMAIL_SETUP_REQUIRED_TOOLTIP,
} from "../../../util/privacyEmailSetup";

export function ContactVendorsModal({
  unclassifiedVendors,
  org,
  collectionContext = "CONSUMER",
  onClose,
  onSuccess,
}: {
  unclassifiedVendors: OrganizationDataRecipientDto[];
  org: OrganizationDto;
  collectionContext?: CollectionContext | CollectionContext[];
  onClose: () => void;
  onSuccess: (contactedVendorNames: string[], contactedVendorIds: string[]) => void;
}) {
  const history = useHistory();
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(false);

  const { contactableVendors, notContactableVendors } = useMemo(() => {
    const contactable: OrganizationDataRecipientDto[] = [];
    const notContactable: OrganizationDataRecipientDto[] = [];
    for (const v of unclassifiedVendors) {
      const email = v.email || v.defaultEmail;
      if (email && email.trim()) {
        contactable.push(v);
      } else {
        notContactable.push(v);
      }
    }
    return { contactableVendors: contactable, notContactableVendors: notContactable };
  }, [unclassifiedVendors]);

  const [selectedVendorIds, setSelectedVendorIds] = useState<Set<string>>(
    () => new Set(contactableVendors.map((v) => v.vendorId)),
  );

  const selectedCount = selectedVendorIds.size;
  const allSelected = contactableVendors.length > 0 && selectedCount === contactableVendors.length;

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedVendorIds(new Set(contactableVendors.map((v) => v.vendorId)));
    } else {
      setSelectedVendorIds(new Set());
    }
  };

  const handleSelectVendor = (vendorId: string, checked: boolean) => {
    setSelectedVendorIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(vendorId);
      } else {
        next.delete(vendorId);
      }
      return next;
    });
  };

  const handleSend = async () => {
    if (selectedCount === 0) return;
    setError(false);
    setSending(true);
    try {
      const requests = contactableVendors
        .filter((v) => selectedVendorIds.has(v.vendorId))
        .map((v) => ({
          vendorId: v.vendorId,
          body: getDefaultEmailBodyForVendor(org, v, collectionContext),
        }));
      const res = await Api.organizationVendor.requestVendorDocumentationBulk(org.id, requests);
      if (res?.ok === true) {
        const contacted = contactableVendors.filter((v) => selectedVendorIds.has(v.vendorId));
        onSuccess(
          contacted.map((v) => v.name),
          contacted.map((v) => v.vendorId),
        );
        onClose();
      } else {
        setError(true);
      }
    } catch {
      setError(true);
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    const unblock = history.block(
      "You have unsent vendor contact emails. Are you sure you want to leave?",
    );
    return () => unblock();
  }, [history]);

  const canSendEmail = canSendPrivacyEmail(org);
  const canSend = !sending && selectedCount > 0 && canSendEmail;

  const sendButtonDisabled = !canSendEmail || sending || selectedCount === 0;
  const sendButtonTooltip = !canSendEmail
    ? PRIVACY_EMAIL_SETUP_REQUIRED_TOOLTIP
    : selectedCount === 0
    ? "Select at least one vendor to contact."
    : undefined;

  return (
    <Modal
      title="Contact Vendors"
      body="Select vendors below to be contacted via email regarding relevant classification queries."
      closeButton={true}
      showOverlay={true}
      closeOnOverlayClick={true}
      maxPanelWidth="max-w-[600px]"
      alignText="left"
      contentPadding="px-[24px] pt-5 pb-4"
      onClose={onClose}
    >
      {contactableVendors.length > 0 && (
        <div className="max-h-[300px] overflow-y-auto">
          <div className="px-3 pb-3">
            <SimpleCheckboxControlled
              name="select-all"
              checked={allSelected}
              onChange={handleSelectAll}
              label="Select All"
            />
          </div>
          <ul>
            {contactableVendors.map((vendor) => {
              const missingProps = getMissingVendorDocumentationProperties(
                org,
                vendor,
                collectionContext,
              );
              const missingLabels = missingProps.map((p) => MISSING_DOC_LABELS[p]);
              return (
                <li key={vendor.vendorId} className="px-3 pb-3 text-xs">
                  <div className="flex items-start gap-3">
                    <div className="pt-0.5">
                      <SimpleCheckboxControlled
                        name={`vendor-${vendor.vendorId}`}
                        checked={selectedVendorIds.has(vendor.vendorId)}
                        onChange={(checked) => handleSelectVendor(vendor.vendorId, checked)}
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-neutral-900 flex items-center gap-2">
                        {vendor.logoUrl ? (
                          <img src={vendor.logoUrl} alt={vendor.name} className="h-4 w-4" />
                        ) : null}
                        {vendor.name}
                        {vendor.serviceProviderInfoRequestSentDate &&
                          vendor.serviceProviderInfoRequestSentDate !== "1970-01-01T00:00:00Z" && (
                            <span className="font-normal text-primary-600">
                              Vendor was contacted on{" "}
                              {new Date(
                                vendor.serviceProviderInfoRequestSentDate,
                              ).toLocaleDateString(undefined, {
                                month: "long",
                                day: "numeric",
                                year: "numeric",
                              })}
                            </span>
                          )}
                      </div>
                      {missingLabels.length > 0 && (
                        <div className="mt-1 text-xs text-neutral-600">
                          {missingLabels.join(", ")}
                        </div>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
      {notContactableVendors.length > 0 && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex gap-2">
            <XCircleIcon className="h-5 w-5 shrink-0 text-red-400" />
            <div>
              <p className="text-xs font-medium text-red-800">
                The vendors listed below will not be contacted since they do not have a contact
                email for queries. Add an email within individual vendor settings or contact them
                later.
              </p>
              <ul className="mt-2 list-disc pl-5 text-xs text-red-500">
                {notContactableVendors.map((v) => (
                  <li key={v.vendorId}>{v.name}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
      {error && (
        <div className="mt-4 rounded-md bg-red-50 p-4 text-sm text-red-800">
          There was an error sending the emails.
        </div>
      )}

      <div className="flex justify-end gap-3 pt-4 px-3 pb-3">
        <Button color="tertiary" onClick={onClose}>
          Cancel
        </Button>
        {sendButtonDisabled && sendButtonTooltip ? (
          <Disableable disabled disabledTooltip={sendButtonTooltip} tooltipZIndex={16000}>
            <span
              className="inline-flex justify-center items-center px-4 py-[9px] w-26 h-9.5 text-sm font-medium rounded-md bg-primary-600 border border-transparent text-white opacity-50 cursor-not-allowed shadow-sm"
              role="button"
              aria-disabled="true"
            >
              {sending ? "Sending…" : "Send"}
            </span>
          </Disableable>
        ) : (
          <Button
            color="primary"
            disabled={sendButtonDisabled}
            onClick={canSend ? handleSend : () => {}}
          >
            {sending ? "Sending…" : "Send"}
          </Button>
        )}
      </div>
    </Modal>
  );
}
